import { useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api, type KV, type Project, type Workspace } from "../lib/api";
import { LoadingButton } from "./Spinner";
import ChipInput from "./ChipInput";

interface Props {
  workspace: Workspace;
  project: Project;
  open: boolean;
  onClose: () => void;
}

// Project-level settings. Today this is mainly the default header set —
// every request in the project picks these up at run time, with per-
// request headers overriding by name. Rename also lives here so it
// matches the gear icon in the scope switcher.
export default function ProjectSettingsModal({ workspace, project, open, onClose }: Props) {
  const qc = useQueryClient();
  const [name, setName] = useState(project.name);
  const [headers, setHeaders] = useState<KV[]>(project.default_headers ?? []);
  // Re-sync when the project changes (switching projects while the
  // modal is open, or after a successful save).
  useEffect(() => {
    setName(project.name);
    setHeaders(project.default_headers ?? []);
  }, [project.id, project.name, project.default_headers]);

  const save = useMutation({
    mutationFn: () => api.projects.update(project.id, {
      name: name.trim() || project.name,
      default_headers: headers.filter((h) => h.key || h.value),
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["projects", workspace.id] }),
  });

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Build the same vars/isResolved/resolveValue trio the request editor
  // uses, so the header value chips light up the way they do elsewhere.
  // Without an "active env" concept on this modal we just treat every
  // env-defined name as resolved.
  const envsQ = useMemo(() => api.environments, []);
  const [envVars, setEnvVars] = useState<Record<string, string>>({});
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    envsQ.list(workspace.id).then((r) => {
      if (cancelled) return;
      const merged: Record<string, string> = {};
      for (const env of r.environments) for (const [k, v] of Object.entries(env.vars ?? {})) {
        if (!(k in merged)) merged[k] = v;
      }
      setEnvVars(merged);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [open, workspace.id, envsQ]);
  const envVarNames = useMemo(() => Object.keys(envVars).sort(), [envVars]);
  const isResolved = (n: string) => n in envVars;
  const resolveValue = (n: string) => envVars[n];

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center p-4 bg-[color:var(--backdrop)]"
      onClick={onClose}
    >
      <div
        className="card w-full max-w-3xl h-[min(820px,90vh)] overflow-hidden flex flex-col shadow-[var(--shadow-strong)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
          <div className="flex items-center gap-2 min-w-0">
            <h2 className="text-base font-semibold">Project settings</h2>
            <span className="text-xs muted-strong truncate">— {project.name}</span>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="h-8 w-8 grid place-items-center rounded text-text-faint hover:text-text hover:bg-surface-2"
          >×</button>
        </div>

        <div className="flex-1 min-h-0 overflow-auto scroll-pane p-5 space-y-5 bg-bg">
          <form
            className="card p-5 space-y-3"
            onSubmit={(e) => { e.preventDefault(); save.mutate(); }}
          >
            <h3 className="font-semibold">Details</h3>
            <div className="space-y-2">
              <label className="label">Project name</label>
              <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
          </form>

          <section className="card p-5 space-y-3 min-w-0">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h3 className="font-semibold">Default headers</h3>
                <p className="text-xs muted mt-1">
                  Added to every request in this project at run time. Per-request headers override by name (case-insensitive). Use <code className="mono px-1 rounded bg-surface-2 border border-border">{`{{VAR}}`}</code> to inject env values.
                </p>
              </div>
            </div>
            <KVHeaders
              items={headers}
              onChange={setHeaders}
              vars={envVars}
              envVarNames={envVarNames}
              isResolved={isResolved}
              resolveValue={resolveValue}
            />
          </section>

          <section className="card p-5 space-y-3">
            <h3 className="font-semibold">Documentation portal</h3>
            <p className="text-sm muted">
              Publish a shareable, branded docs site for this project — every collection, request, and flow
              doc in one navigable page, with an OpenAPI export. Choose public or email-verified access.
            </p>
            <button
              type="button"
              className="btn"
              onClick={() => { onClose(); (window as unknown as { __openDocsSite?: () => void }).__openDocsSite?.(); }}
            >Open portal settings →</button>
          </section>

          <div className="flex justify-end gap-2">
            <button type="button" className="btn" onClick={onClose}>Cancel</button>
            <LoadingButton
              type="button"
              className="btn-primary"
              pending={save.isPending}
              pendingLabel="Saving…"
              onClick={() => save.mutate()}
            >Save changes</LoadingButton>
          </div>
        </div>
      </div>
    </div>
  );
}

// Minimal KV editor — same grid template as the request builder's
// Params/Headers but local to this modal so it doesn't drag in the
// full env tooltip logic from the URL bar.
function KVHeaders({
  items, onChange, vars, envVarNames, isResolved, resolveValue,
}: {
  items: KV[];
  onChange: (v: KV[]) => void;
  vars: Record<string, string>;
  envVarNames: string[];
  isResolved: (n: string) => boolean;
  resolveValue: (n: string) => string | undefined;
}) {
  const set = (i: number, patch: Partial<KV>) => onChange(items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  const remove = (i: number) => onChange(items.filter((_, idx) => idx !== i));
  const addRow = () => onChange([...items, { key: "", value: "", enabled: true }]);
  return (
    <div className="space-y-1 min-w-0">
      {items.length > 0 && (
        <div className="grid grid-cols-[28px_minmax(0,1fr)_minmax(0,1fr)_28px] gap-2 px-2 text-[11px] uppercase tracking-wider muted-strong">
          <div></div>
          <div>Header</div>
          <div>Value</div>
          <div></div>
        </div>
      )}
      {items.map((it, i) => (
        <div key={i} className="grid grid-cols-[28px_minmax(0,1fr)_minmax(0,1fr)_28px] gap-2 items-center">
          <input
            type="checkbox"
            checked={it.enabled !== false}
            onChange={(e) => set(i, { enabled: e.target.checked })}
            className="h-4 w-4 rounded justify-self-center"
          />
          <ChipInput
            value={it.key}
            onChange={(v) => set(i, { key: v })}
            suggestions={envVarNames.map((n) => ({ name: n, hint: vars[n] ?? "" }))}
            isResolved={isResolved}
            resolveValue={resolveValue}
            placeholder="Header"
            spellCheck={false}
          />
          <ChipInput
            value={it.value}
            onChange={(v) => set(i, { value: v })}
            suggestions={envVarNames.map((n) => ({ name: n, hint: vars[n] ?? "" }))}
            isResolved={isResolved}
            resolveValue={resolveValue}
            placeholder="Value"
            spellCheck={false}
          />
          <button
            type="button"
            onClick={() => remove(i)}
            className="h-7 w-7 grid place-items-center rounded text-text-faint hover:text-danger hover:bg-surface-2 justify-self-center"
            title="Remove"
            aria-label="Remove"
          >✕</button>
        </div>
      ))}
      {items.length === 0 && (
        <div className="empty-card">No default headers yet. Click <span className="empty-card-strong">+ Add header</span> to add one.</div>
      )}
      <div className="pt-1">
        <button type="button" className="btn" onClick={addRow}>＋ Add header</button>
      </div>
    </div>
  );
}
